<template>
  <window
    :size="windowSize"
    :title="windowTitle"
    :paddingBottom="btnMargin != '0'"
  >
    <editable
      ref="editable"
      class="job-fittings-list container-fluid table"
      :default-state="allowEdit ? 'edit' : 'show'"
      :show-toggle-button="false"
      :children-always-multiple="true"
    >
      <div class="job-fittings-list__header table__head row">
        <div class="col col-7 job-fittings-list__col-type">
          {{ __("jobs.components.fittings.list.header.title") }}
        </div>
        <div class="col col-1 job-fittings-list__col-amount">
          {{ __("jobs.components.fittings.list.header.amount") }}
        </div>
        <div class="col col-2 job-fittings-list__col-unit">
          {{ __("jobs.components.fittings.list.header.unit") }}
        </div>
        <div class="col col-1 job-fittings-list__col-status text-center">
          {{ __("jobs.components.fittings.list.header.status") }}
        </div>
        <div class="col col-1 job-fittings-list__col-message text-center">
          {{ __("jobs.components.fittings.list.header.message") }}
        </div>
      </div>

      <ul class="job-fittings-list__list table__body list-reset">
        <li
          v-for="fitting in fittings"
          :key="fitting.id"
          class="table__row row"
        >
          <collapsible
            :use-default-toggle-button="false"
            :open-on-head-click="false"
            class="container-fluid"
          >
            <editable
              slot="head"
              class="row"
              name="fitting"
              :additional-data="{ type: fitting.type }"
              :default-state="allowEdit ? 'edit' : 'show'"
              :show-toggle-button="false"
            >
              <span class="col col-7 job-fittings-list__col-type table__col">
                {{ fitting.title }}
              </span>
              <span class="col col-1 job-fittings-list__col-amount table__col">
                {{ fitting.amount }}
              </span>
              <span class="col col-2 job-fittings-list__col-unit table__col">
                {{
                  fitting.unit
                    ? fitting.unit
                    : __(
                        "jobs.components.fittings.list.common.fitting_unit_default"
                      )
                }}
              </span>
              <span
                class="
                  col col-1
                  job-fittings-list__col-status
                  table__col table__col--v-center table__col--h-center
                "
              >
                <editable-check
                  name="status"
                  :value="fitting.status == 1 || fitting.status == 2 ? 1 : 0"
                  :icon-src="getIconSrc"
                ></editable-check>
              </span>
              <span
                class="
                  col col-1
                  job-fittings-list__col-message
                  table__col table__col--v-center table__col--h-center
                "
              >
                <template v-if="!allowEdit && fitting.status > 1">
                  <span
                    :is="
                      typeof messages[fitting.type] == 'object' &&
                      messages[fitting.type].url
                        ? 'a'
                        : 'span'
                    "
                    :href="messages[fitting.type].url"
                  >
                    <img :src="getIconSrc('warning-important.svg')" />
                  </span>

                  <popover
                    target="img"
                    relative-to="$parent"
                    :show-on-mounted="false"
                    :show-on-updated="false"
                    :toggle-on="['mouseenter', 'mouseleave']"
                    type="info"
                  >
                    <template v-if="typeof messages[fitting.type] == 'object'">
                      <div class="d-flex nowrap mr-md">
                        <div class="mr-md">
                          {{ messages[fitting.type].user }}
                        </div>
                        <div>{{ messages[fitting.type].date }}</div>
                      </div>

                      <p v-html="messages[fitting.type].text" class="mt-xs"></p>
                    </template>
                    <template v-else>{{ messages[fitting.type] }}</template>
                  </popover>
                </template>

                <button
                  v-else-if="allowEdit"
                  @click="createMessage(fitting, $event)"
                >
                  <img
                    v-if="fitting.status > 1"
                    :src="getIconSrc('warning-important.svg')"
                  />
                  <img v-else :src="getIconSrc('note.svg')" />
                </button>
              </span>
            </editable>
          </collapsible>
        </li>
      </ul>

      <div class="row mt-md" v-if="allowEdit">
        <div class="col col-10 text-right">
          Alle kontrolliert und vollständig:
        </div>
        <div class="col col-1 d-flex justify-content-center align-items-end">
          <editable-check
            name="all"
            :icon-src="getIconSrc"
            @user-input="toggleAll($event.data)"
          ></editable-check>
        </div>
        <div class="col col-1"></div>
      </div>
    </editable>

    <div slot="footer" class="buttons text-right" slot-scope="window">
      <div class="container-fluid mb-xl">
        <div class="row">
          <div class="col col-6"></div>
          <div class="col col-6">
            Sie sind verantwortlich für die Vollständigkeit der Beschläge.<br />
            Gehen Sie auf Nummer sicher, bevor Sie auf die Baustelle fahren.
          </div>
        </div>
      </div>

      <button
        class="btn btn-secondary"
        @click="window.hide()"
        :style="'margin-bottom: ' + btnMargin"
      >
        Abbrechen
      </button>

      <button
        class="btn btn-next"
        @click="saveAndClose(window)"
        :style="'margin-bottom: ' + btnMargin"
      >
        Kontrolle abschließen
      </button>
    </div>
  </window>
</template>

<script>
import NeedsTranslations from "../Mixins/NeedsTranslations";
import NeedsIcons from "../Mixins/NeedsIcons";
import JobFittingMessageWindow from "./JobFittingMessage";

export default {
  mixins: [NeedsTranslations, NeedsIcons],
  provide: {
    windowId: "jobFittingsList",
  },
  props: {
    windowSize: {
      type: String,
      default: "lg",
    },
    windowTitle: {
      type: String,
    },
    fittings: {
      type: Array,
      required: true,
    },
    fittingCategories: {
      type: Array,
    },
    messages: {
      type: Object,
      default() {
        return {};
      },
    },
    allowEdit: {
      type: Boolean,
      default: false,
    },
    btnMargin: {
      type: String,
      default: "0",
    },
  },
  data() {
    return {
      messagesChanged: false,
    };
  },
  methods: {
    toggleAll(check) {
      this.$refs.editable.childrenInstances.fitting.forEach((child) => {
        child.childrenInstances.status[0].setData(check);
        child.childrenInstances.status[0].dataChanged(true);
      });
    },
    async createMessage(fitting, $triggerEvent = null) {
      const message = {
        text: fitting.type in this.messages ? this.messages[fitting.type] : "",
      };

      const result = await this.$windows.showWindow(
        JobFittingMessageWindow,
        { fitting, message },
        $triggerEvent ? $triggerEvent.target : null
      );

      if (result && result.text.length) {
        fitting.status = fitting.status == 1 ? 2 : 3;
        this.messages[fitting.type] = result.text;
        this.messagesChanged = true;
      }
    },
    saveAndClose(window) {
      const fittings = this.$refs.editable.getData()["fitting"];

      const messages = this.messagesChanged
        ? Object.entries(this.messages).map(([type, message]) => ({
            type,
            message,
          }))
        : [];

      for (const fitting of fittings) {
        if (fitting.type in this.messages) {
          if (fitting.status != 2) {
            fitting.status = fitting.status == 1 ? 2 : 3;
          }
        }
      }

      window.hide({ fittings, messages });
    },
  },
};
</script>